import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { registerUser, clearState } from "../redux/slieces/registrationSlice";
import { Form, Input, Button, Typography, Alert, Select } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useKeycloak } from "@react-keycloak/web";
import "./RegisterForm.module.css";
import { useTranslation } from "react-i18next";
import notificationBus from "../events/notificationBus";
import logger from "../core/utils/logger";


const { Title } = Typography;
const RegisterForm = () => {
    const dispatch=useDispatch();
    const navigate = useNavigate();
    const { keycloak, initialized } = useKeycloak();
    const { t } = useTranslation()

  // Title options for the dropdown
  const titleOptions = [
    { value: "", label: t("register.no_title") },
    { value: "Dr.", label: "Dr." },
    { value: "Prof.", label: "Prof." },
    { value: "Prof. Dr.", label: "Prof. Dr." },
    { value: "Dr. med.", label: "Dr. med." },
    { value: "Prof. Dr. med.", label: "Prof. Dr. med." },
    { value: "Mr.", label: t("register.mr") },
    { value: "Mrs.", label: t("register.mrs") },
    { value: "Ms.", label: t("register.ms") },
    { value: "Mx.", label: t("register.mx") }
  ];

  // Use useSearchParams to get the organisationID from the URL if present.
  const [searchParams] = useSearchParams();

  const organisationIDFromQuery = searchParams.get("organizationId");
  logger.log("Organisation ID from query:", organisationIDFromQuery);
  const email = searchParams.get("email");

  const { loading, error, organisationId, message } = useSelector(
    (state) => state.registration.registration
  );
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });
  const [form] = Form.useForm();
  const recaptchaRef = useRef(null);
  useEffect(() => {
    dispatch(clearState());
  }, [dispatch]);
  useEffect(() => {
    if (email) {
      form.setFieldsValue({ email });
    }
  }, [email, form]);
  /*  useEffect(() => {
        if (message) {
          // Display the success message for 2 seconds before redirecting.
          setTimeout(() => {
            navigate("/");
          }, 2000);
        }
      }, [message, navigate]); */

      useEffect(() => {
        if (message) {
          
          notificationBus.emit('notify',{
            type:'success',
            message: t("register.success"),
          });
      
          // Optional redirect after a short delay
          setTimeout(() => {
            navigate("/");
          }, 5000);
        }
      }, [message, navigate]);

      // Show error notification
  useEffect(() => {
    if (error) {
      notificationBus.emit('notify', {
        type: "error",
        message: t("register.error", { error }),
      });
      recaptchaRef.current.reset(); // Reset reCAPTCHA on error
    }
  }, [error]);
      

  const onFinish = (values) => {
    // Get the reCAPTCHA token from the widget.
    const recaptchaToken = recaptchaRef.current.getValue();
    if (!recaptchaToken) {
      // Optionally, you could display an error message here.
      console.error("Please complete the captcha");
      return;
    }

    // Add the reCAPTCHA token to the form values.
    const payload = { ...values, recaptchaToken };
    if (organisationIDFromQuery) {
      payload.organizationId = organisationIDFromQuery;
      payload.licenseKey = "no admin needed"; // Include license key if provided
    }
    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
    dispatch(registerUser(payload));
    };
    // Check if the user is already authenticated
    if(keycloak.authenticated){
        return <div>{t("register.already_authenticated")} </div>;
    }
    

    return (
        <div className="registration-container">
        <Title level={2}>{t("register.title")}</Title>
        {/* Display Alerts for status messages */}
        {loading && <Alert message={t("register.loading",{loading})} type="info" showIcon />}
        {error && <Alert message={t("register.error",{error})} type="error" showIcon />}
        {message && <Alert message={t("register.success")} type="success" showIcon />}
  
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="registration-form"
          initialValues={{ email: email || "" }}
        >
          <Form.Item
            label={t("register.user_title")}
            name="title"
          >
            <Select placeholder={t("register.select_title_placeholder")}>
              {titleOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t("register.firstname")}
            name="firstName"
          >
            <Input placeholder={t("register.firstname")} />
          </Form.Item>

          <Form.Item
            label={t("register.surname")}
            name="surname"
          >
            <Input placeholder={t("register.surname")} />
          </Form.Item>

          <Form.Item
            label={t("register.username")}
            name="username"
            rules={[{ required: true, message: t("register.validation.username_required") }]}
          >
            <Input placeholder={t("register.username")} />
          </Form.Item>
  
          <Form.Item
            label={t("register.email")}
            name="email"
            rules={[
              { required: true, message: t("register.validation.email_required") },
              { type: "email", message: t("register.validation.email_invalid") },
            ]}
          >
            <Input placeholder={t("register.email")} readOnly={!!email} />
          </Form.Item>
  
          <Form.Item
            label={t("register.password")}
            name="password"
            rules={[{ required: true, message: t("register.validation.password_required") }]}
          >
            <Input.Password placeholder={t("register.password")} />
          </Form.Item>
          {/* Admin sign-up path: license key is optional. Admins can leave
              it blank and add the key later from the LicenseSection on the
              Organisation Settings page. The existing LicenseGuard keeps
              invites blocked (status NoLicense → 402) until a key is
              activated, so the consequence is already enforced server-side
              — no need to require it here. Invited users (when
              organisationIDFromQuery is present) never see this field. */}
          { !organisationIDFromQuery && (<Form.Item
          label={t("register.License_key_contact")}
          name="licenseKey"
          extra={t("register.License_key_help")}
        >
          <Input.TextArea
            rows={5}
            placeholder={t("register.License_key_placeholder")}
            autoSize={{ minRows: 5, maxRows: 10 }}
          />
        </Form.Item>
        )}
        <Form.Item>
          {/* Place the reCAPTCHA widget */}
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6Lf3-tAqAAAAAEQ-BA1Bf7QNnQH-R9XMK1JkFy3B" // Replace with your Site Key
          />
        </Form.Item>
  
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t("register.button")}
            </Button>
          </Form.Item>
        </Form>
  
         {/* Optionally, display the organisation ID from the invitation link */}
      {organisationIDFromQuery && (
        <div className="organisation-id">
          <p>{t("register.organization_id_from_link")} {organisationIDFromQuery}</p>
        </div>
      )}

      {/* If no organisationID in URL, you might be creating one from admin registration */}
      {organisationId && !organisationIDFromQuery && (
        <div className="organisation-id">
          <p>{t("register.organization_id_created")} {organisationId}</p>
        </div>
      )}
    </div>
  );
};

export default RegisterForm;
